// frontend/src/components/Dashboard/ShipmentForm.jsx
import React, { useState, useEffect, useCallback } from "react";
import { createShipment, getAllOrders, getProducts } from "../../api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShip,
  faBox,
  faTruck,
  faPlane,
  faTimes,
  faPlus,
  faTrash,
  faSave,
  faSpinner,
  faCheck,
  faExclamationTriangle,
  faInfoCircle,
  faMapMarkerAlt,
  faCalendar,
  faWeightScale,
  faBoxes,
  faUser,
  faEnvelope,
  faPhone,
  faGlobe,
  faAnchor,
  faClipboard,
  faWarehouse,
  faCity,
  faRoad,
  faTrain,
  faShip as faShipIcon,
  faBuilding,
  faLocationDot,
  faCheckCircle,
  faMobileAlt,
  faMoneyBillWave,
  faCreditCard,
  faWallet,
  faStore,
  faCrown,
  faShieldAlt,
  faUserCheck,
  faList,
  faSlidersH,
  faTags,
  faLayerGroup,
  faPalette,
  faEye,
  faEyeSlash,
  faCheckDouble,
  faClock,
  faRocket,
  faLeaf,
  faCow,
  faTractor,
  faShoppingCart,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

// ============================================
// BRAND & SUPPLIER CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

const SUPPLIER = {
  name: "Mylasha",
  country: "Malaysia",
  city: "Kuala Lumpur",
  portLoading: "Port Klang, Malaysia",
  portDischarge: "Freetown, Sierra Leone",
};

// ============================================
// MAIN COMPONENT
// ============================================
const ShipmentForm = ({ onClose, onSave, initialOrderId = null }) => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [formData, setFormData] = useState({
    orderId: initialOrderId || "",
    orderType: "retail",
    origin: {
      city: SUPPLIER.city,
      country: SUPPLIER.country,
      warehouse: `${SUPPLIER.name} Warehouse`,
    },
    destination: {
      city: "Freetown",
      country: "Sierra Leone",
      district: "",
    },
    estimatedDelivery: "",
    shippingMethod: "sea",
    bulkDetails: {
      isBulkShipment: false,
      containerNumber: "",
      containerType: "LCL",
      vesselName: "",
      voyageNumber: "",
      portOfLoading: SUPPLIER.portLoading,
      portOfDischarge: SUPPLIER.portDischarge,
      blNumber: "",
      sealNumber: "",
      weightInKg: 0,
      palletsCount: 0,
    },
    smallShipmentDetails: {
      courierName: "",
      courierTrackingNumber: "",
      packageWeight: 0,
      packageDimensions: { length: 0, width: 0, height: 0 },
    },
    products: [],
    customs: {
      cleared: false,
      clearanceDate: "",
      customsBroker: "",
      dutiesPaid: false,
      dutyAmount: 0,
    },
    notes: "",
    internalNotes: "",
    trackingUrl: "",
    supplier: {
      name: SUPPLIER.name,
      location: SUPPLIER.city,
      country: SUPPLIER.country,
    },
    shippingAddress: {
      recipientName: "",
      street: "",
      city: "",
      district: "",
      country: "Sierra Leone",
      phone: "",
    },
    customer: {
      name: "",
      email: "",
      phone: "",
      whatsapp: "",
      userType: "customer",
      farmName: "",
    },
  });

  const [productSearch, setProductSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productQuantity, setProductQuantity] = useState(1);
  const [showProductSelector, setShowProductSelector] = useState(false);

  // Fetch orders and products on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ordersRes, productsRes] = await Promise.all([
          getAllOrders({ limit: 100 }),
          getProducts({ limit: 100, isAvailable: true }),
        ]);

        const ordersData = ordersRes?.data?.orders || ordersRes?.data || [];
        const productsData =
          productsRes?.data?.products || productsRes?.data || [];

        setOrders(Array.isArray(ordersData) ? ordersData : []);
        setProducts(Array.isArray(productsData) ? productsData : []);

        if (initialOrderId) {
          const order = ordersData.find((o) => o._id === initialOrderId);
          if (order) handleOrderSelect(initialOrderId);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load orders and products");
      }
    };
    fetchData();
  }, [initialOrderId]);

  const handleOrderSelect = (orderId) => {
    const order = orders.find((o) => o._id === orderId);
    if (!order) return;

    setSelectedOrder(order);
    const isBulk =
      order.orderType === "bulk_farmer" || (order.totalQuantity || 0) >= 100;

    setFormData((prev) => ({
      ...prev,
      orderId: orderId,
      orderType: isBulk ? "bulk_farmer" : "retail",
      products:
        order.items?.map((item) => ({
          productId: item.product?._id || item.product,
          name: item.name,
          quantity: item.quantity,
          unit: item.unit || "kg",
          price: item.price?.usd || 0,
        })) || [],
      totalQuantity: order.totalQuantity || 0,
      totalWeight: order.totalQuantity || 0,
      bulkDetails: {
        ...prev.bulkDetails,
        weightInKg: order.totalQuantity || 0,
        palletsCount: Math.ceil((order.totalQuantity || 0) / 500),
        isBulkShipment: isBulk,
        portOfLoading: SUPPLIER.portLoading,
        portOfDischarge: SUPPLIER.portDischarge,
      },
      shippingAddress: {
        recipientName:
          order.shippingAddress?.recipientName || order.userName || "",
        street: order.shippingAddress?.street || "",
        city: order.shippingAddress?.city || "",
        district: order.shippingAddress?.district || "",
        country: order.shippingAddress?.country || "Sierra Leone",
        phone: order.shippingAddress?.phone || order.userPhone || "",
      },
      customer: {
        name: order.userName || "",
        email: order.userEmail || "",
        phone: order.userPhone || "",
        whatsapp: order.userWhatsapp || order.userPhone || "",
        userType: order.userType || "customer",
        farmName: order.farmerDetails?.farmName || "",
      },
      supplier: {
        name: SUPPLIER.name,
        location: SUPPLIER.city,
        country: SUPPLIER.country,
      },
      origin: {
        city: SUPPLIER.city,
        country: SUPPLIER.country,
        warehouse: `${SUPPLIER.name} Warehouse`,
      },
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes(".")) {
      const parts = name.split(".");
      if (parts.length === 2) {
        const [parent, child] = parts;
        setFormData((prev) => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: type === "checkbox" ? checked : value,
          },
        }));
      } else if (parts.length === 3) {
        const [parent, child, grandchild] = parts;
        setFormData((prev) => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: {
              ...prev[parent]?.[child],
              [grandchild]: type === "checkbox" ? checked : value,
            },
          },
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
    setError("");
  };

  const handleAddProduct = () => {
    if (!selectedProduct) {
      setError("Please select a product");
      return;
    }
    if (productQuantity <= 0) {
      setError("Quantity must be greater than 0");
      return;
    }

    const existingIndex = formData.products.findIndex(
      (p) => p.productId === selectedProduct._id,
    );

    if (existingIndex >= 0) {
      setFormData((prev) => ({
        ...prev,
        products: prev.products.map((p, idx) =>
          idx === existingIndex
            ? { ...p, quantity: p.quantity + productQuantity }
            : p,
        ),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        products: [
          ...prev.products,
          {
            productId: selectedProduct._id,
            name: selectedProduct.name,
            quantity: productQuantity,
            unit: selectedProduct.unit || "kg",
            price:
              selectedProduct.basePrice?.usd ||
              selectedProduct.pricing?.usd ||
              0,
            origin: selectedProduct.origin || SUPPLIER.country,
          },
        ],
      }));
    }

    setSelectedProduct(null);
    setProductQuantity(1);
    setProductSearch("");
    setShowProductSelector(false);
  };

  const handleRemoveProduct = (index) => {
    setFormData((prev) => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index),
    }));
  };

  const handleProductSearch = (value) => {
    setProductSearch(value);
    const found = products.find(
      (p) =>
        p.name.toLowerCase().includes(value.toLowerCase()) ||
        p.slug?.toLowerCase().includes(value.toLowerCase()),
    );
    setSelectedProduct(found || null);
  };

  const calculateTotalWeight = useCallback(() => {
    return formData.products.reduce((sum, p) => sum + (p.quantity || 0), 0);
  }, [formData.products]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    if (!formData.orderId && formData.products.length === 0) {
      setError("Either select an order or add products");
      setLoading(false);
      return;
    }

    try {
      const totalWeight = calculateTotalWeight();

      const shipmentData = {
        orderId: formData.orderId || undefined,
        orderType: formData.orderType,
        origin: formData.origin,
        destination: formData.destination,
        estimatedDelivery: formData.estimatedDelivery || null,
        shippingMethod: formData.shippingMethod,
        products: formData.products.map((p) => ({
          productId: p.productId,
          name: p.name,
          quantity: p.quantity,
          unit: p.unit || "kg",
          price: p.price || 0,
          origin: p.origin || SUPPLIER.country,
        })),
        totalQuantity: totalWeight,
        totalWeight: totalWeight,
        shippingAddress: formData.shippingAddress,
        customer: formData.customer,
        supplier: formData.supplier,
        notes: formData.notes,
        internalNotes: formData.internalNotes,
        trackingUrl: formData.trackingUrl,
        customs: formData.customs,
      };

      if (
        formData.orderType === "bulk_farmer" ||
        formData.bulkDetails.isBulkShipment
      ) {
        shipmentData.bulkDetails = {
          isBulkShipment: true,
          containerNumber: formData.bulkDetails.containerNumber,
          containerType: formData.bulkDetails.containerType || "LCL",
          vesselName: formData.bulkDetails.vesselName,
          voyageNumber: formData.bulkDetails.voyageNumber,
          portOfLoading:
            formData.bulkDetails.portOfLoading || SUPPLIER.portLoading,
          portOfDischarge:
            formData.bulkDetails.portOfDischarge || SUPPLIER.portDischarge,
          blNumber: formData.bulkDetails.blNumber,
          sealNumber: formData.bulkDetails.sealNumber,
          weightInKg: formData.bulkDetails.weightInKg || totalWeight,
          palletsCount:
            formData.bulkDetails.palletsCount || Math.ceil(totalWeight / 500),
        };
      }

      if (formData.shippingMethod === "courier") {
        shipmentData.smallShipmentDetails = {
          courierName: formData.smallShipmentDetails.courierName,
          courierTrackingNumber:
            formData.smallShipmentDetails.courierTrackingNumber,
          packageWeight:
            formData.smallShipmentDetails.packageWeight || totalWeight,
          packageDimensions: formData.smallShipmentDetails
            .packageDimensions || {
            length: 0,
            width: 0,
            height: 0,
          },
        };
      }

      const response = await createShipment(shipmentData);

      if (response.data.success) {
        setSuccess("Shipment created successfully!");
        setTimeout(() => {
          if (onSave) onSave(response.data.data);
          if (onClose) onClose();
        }, 1500);
      }
    } catch (error) {
      console.error("Error creating shipment:", error);
      setError(
        error.response?.data?.message ||
          error.message ||
          "Error creating shipment. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const isBulkOrder =
    formData.orderType === "bulk_farmer" || formData.bulkDetails.isBulkShipment;
  const isCourier = formData.shippingMethod === "courier";

  const availableOrders = orders.filter(
    (o) => o.status !== "delivered" && o.status !== "cancelled",
  );

  const filteredProducts = products.filter(
    (p) =>
      p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
      p.slug?.toLowerCase().includes(productSearch.toLowerCase()),
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <FontAwesomeIcon icon={faShip} /> Create Shipment
            <span className="supplier-tag">
              <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.name}
            </span>
          </h2>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {error && (
          <div className="alert-message error">
            <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
          </div>
        )}
        {success && (
          <div className="alert-message success">
            <FontAwesomeIcon icon={faCheck} /> {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="shipment-form">
          {/* Order Selection */}
          <div className="form-section">
            <h3>
              <FontAwesomeIcon icon={faBox} /> Order Information
            </h3>
            <div className="form-row">
              <div className="form-group">
                <label>Select Order (Optional)</label>
                <select
                  value={formData.orderId}
                  onChange={(e) => handleOrderSelect(e.target.value)}
                >
                  <option value="">-- Create Inventory Shipment --</option>
                  {availableOrders.map((order) => (
                    <option key={order._id} value={order._id}>
                      {order.orderNumber} -{" "}
                      {order.userName || order.customerName || "Unknown"}
                      {order.orderType === "bulk_farmer" ? " 🌾 BULK" : ""}
                      {order.totalQuantity ? ` (${order.totalQuantity}kg)` : ""}
                      {order.origin === SUPPLIER.country && " 🇲🇾"}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Order Type</label>
                <select
                  name="orderType"
                  value={formData.orderType}
                  onChange={handleChange}
                >
                  <option value="retail">🛒 Retail</option>
                  <option value="bulk_farmer">🌾 Bulk Farmer</option>
                  <option value="wholesale">📦 Wholesale</option>
                  <option value="inventory">📦 Inventory</option>
                </select>
              </div>
            </div>
          </div>

          {/* Supplier & Origin Info */}
          <div
            className="form-section"
            style={{ borderLeft: `4px solid ${BRAND.secondary}` }}
          >
            <h3>
              <FontAwesomeIcon icon={faShip} /> Supplier & Origin
            </h3>
            <div className="supplier-info-card">
              <FontAwesomeIcon icon={faShip} className="supplier-icon" />
              <div className="supplier-info">
                <strong>{SUPPLIER.name}</strong>
                <span>
                  <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country} &bull;
                  <FontAwesomeIcon icon={faAnchor} /> {SUPPLIER.portLoading}
                </span>
              </div>
              <span className="supplier-badge">
                <FontAwesomeIcon icon={faCheckCircle} /> Quality Assured
              </span>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faGlobe} /> Origin Country
                </label>
                <input
                  type="text"
                  name="origin.country"
                  value={formData.origin?.country || SUPPLIER.country}
                  onChange={handleChange}
                  placeholder="Country of origin"
                />
                <small className="helper-text">Default: {SUPPLIER.country}</small>
              </div>
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faCity} /> Origin City
                </label>
                <input
                  type="text"
                  name="origin.city"
                  value={formData.origin?.city || SUPPLIER.city}
                  onChange={handleChange}
                  placeholder="City of origin"
                />
              </div>
            </div>
            <div className="form-group">
              <label>
                <FontAwesomeIcon icon={faWarehouse} /> Warehouse
              </label>
              <input
                type="text"
                name="origin.warehouse"
                value={formData.origin?.warehouse || `${SUPPLIER.name} Warehouse`}
                onChange={handleChange}
                placeholder="Warehouse name"
              />
            </div>
          </div>

          {/* Customer & Shipping Info */}
          <div className="form-section">
            <h3>
              <FontAwesomeIcon icon={faUser} /> Customer & Shipping
            </h3>
            <div className="form-row">
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faUser} /> Customer Name
                </label>
                <input
                  type="text"
                  name="customer.name"
                  value={formData.customer?.name || ""}
                  onChange={handleChange}
                  placeholder="Customer name"
                />
              </div>
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faEnvelope} /> Customer Email
                </label>
                <input
                  type="email"
                  name="customer.email"
                  value={formData.customer?.email || ""}
                  onChange={handleChange}
                  placeholder="customer@example.com"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faPhone} /> Customer Phone
                </label>
                <input
                  type="tel"
                  name="customer.phone"
                  value={formData.customer?.phone || ""}
                  onChange={handleChange}
                  placeholder="+232 76 123456"
                />
              </div>
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faWhatsapp} /> Customer WhatsApp
                </label>
                <input
                  type="tel"
                  name="customer.whatsapp"
                  value={formData.customer?.whatsapp || ""}
                  onChange={handleChange}
                  placeholder="+232 76 123456"
                />
                <small className="helper-text">For shipping updates</small>
              </div>
            </div>
            <div className="form-group">
              <label>User Type</label>
              <select
                name="customer.userType"
                value={formData.customer?.userType || "customer"}
                onChange={handleChange}
              >
                <option value="customer">🛒 Customer</option>
                <option value="farmer">🌾 Farmer</option>
              </select>
            </div>
            <div className="form-group">
              <label>Farm Name (if applicable)</label>
              <input
                type="text"
                name="customer.farmName"
                value={formData.customer?.farmName || ""}
                onChange={handleChange}
                placeholder="Farm name"
              />
            </div>

            <h4>
              <FontAwesomeIcon icon={faMapMarkerAlt} /> Shipping Address
            </h4>
            <div className="form-group">
              <label>Recipient Name</label>
              <input
                type="text"
                name="shippingAddress.recipientName"
                value={formData.shippingAddress?.recipientName || ""}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Street Address</label>
              <input
                type="text"
                name="shippingAddress.street"
                value={formData.shippingAddress?.street || ""}
                onChange={handleChange}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  name="shippingAddress.city"
                  value={formData.shippingAddress?.city || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>District</label>
                <input
                  type="text"
                  name="shippingAddress.district"
                  value={formData.shippingAddress?.district || ""}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Country</label>
              <input
                type="text"
                name="shippingAddress.country"
                value={formData.shippingAddress?.country || "Sierra Leone"}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>
                <FontAwesomeIcon icon={faPhone} /> Shipping Phone
              </label>
              <input
                type="tel"
                name="shippingAddress.phone"
                value={formData.shippingAddress?.phone || ""}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Shipping Method */}
          <div className="form-section">
            <h3>
              <FontAwesomeIcon icon={faTruck} /> Shipping Method
            </h3>
            <div className="form-row">
              <div className="form-group">
                <label>Shipping Method</label>
                <select
                  name="shippingMethod"
                  value={formData.shippingMethod}
                  onChange={handleChange}
                >
                  <option value="sea">
                    <FontAwesomeIcon icon={faShipIcon} /> Sea Freight (Economy)
                  </option>
                  <option value="air">
                    <FontAwesomeIcon icon={faPlane} /> Air Freight (Express)
                  </option>
                  <option value="road">
                    <FontAwesomeIcon icon={faRoad} /> Road Transport
                  </option>
                  <option value="courier">
                    <FontAwesomeIcon icon={faBox} /> Courier
                  </option>
                  <option value="combined">
                    <FontAwesomeIcon icon={faTrain} /> Combined
                  </option>
                </select>
              </div>
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faCalendar} /> Estimated Delivery
                </label>
                <input
                  type="date"
                  name="estimatedDelivery"
                  value={formData.estimatedDelivery}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Bulk Shipping Details */}
          {isBulkOrder && (
            <div className="form-section bulk-section">
              <h3>
                <FontAwesomeIcon icon={faShipIcon} /> Bulk Shipping Details
                <span className="bulk-tag">
                  <FontAwesomeIcon icon={faAnchor} /> {SUPPLIER.portLoading}
                </span>
              </h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Container Number</label>
                  <input
                    type="text"
                    name="bulkDetails.containerNumber"
                    value={formData.bulkDetails?.containerNumber || ""}
                    onChange={handleChange}
                    placeholder="e.g., MSCU1234567"
                  />
                </div>
                <div className="form-group">
                  <label>Container Type</label>
                  <select
                    name="bulkDetails.containerType"
                    value={formData.bulkDetails?.containerType || "LCL"}
                    onChange={handleChange}
                  >
                    <option value="LCL">LCL (Less than Container)</option>
                    <option value="20ft">20ft Container</option>
                    <option value="40ft">40ft Container</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Vessel Name</label>
                  <input
                    type="text"
                    name="bulkDetails.vesselName"
                    value={formData.bulkDetails?.vesselName || ""}
                    onChange={handleChange}
                    placeholder="e.g., MAERSK CHICAGO"
                  />
                </div>
                <div className="form-group">
                  <label>Voyage Number</label>
                  <input
                    type="text"
                    name="bulkDetails.voyageNumber"
                    value={formData.bulkDetails?.voyageNumber || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faAnchor} /> Port of Loading
                  </label>
                  <input
                    type="text"
                    name="bulkDetails.portOfLoading"
                    value={
                      formData.bulkDetails?.portOfLoading ||
                      SUPPLIER.portLoading
                    }
                    onChange={handleChange}
                  />
                  <small className="helper-text">
                    Default: {SUPPLIER.portLoading}
                  </small>
                </div>
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faAnchor} /> Port of Discharge
                  </label>
                  <input
                    type="text"
                    name="bulkDetails.portOfDischarge"
                    value={
                      formData.bulkDetails?.portOfDischarge ||
                      SUPPLIER.portDischarge
                    }
                    onChange={handleChange}
                  />
                  <small className="helper-text">
                    Default: {SUPPLIER.portDischarge}
                  </small>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>B/L Number</label>
                  <input
                    type="text"
                    name="bulkDetails.blNumber"
                    value={formData.bulkDetails?.blNumber || ""}
                    onChange={handleChange}
                    placeholder="Bill of Lading number"
                  />
                </div>
                <div className="form-group">
                  <label>Seal Number</label>
                  <input
                    type="text"
                    name="bulkDetails.sealNumber"
                    value={formData.bulkDetails?.sealNumber || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faWeightScale} /> Weight (kg)
                  </label>
                  <input
                    type="number"
                    name="bulkDetails.weightInKg"
                    value={formData.bulkDetails?.weightInKg || 0}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faBoxes} /> Pallets Count
                  </label>
                  <input
                    type="number"
                    name="bulkDetails.palletsCount"
                    value={formData.bulkDetails?.palletsCount || 0}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
              </div>
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="bulkDetails.isBulkShipment"
                    checked={formData.bulkDetails?.isBulkShipment || false}
                    onChange={handleChange}
                  />
                  This is a Bulk Shipment
                </label>
              </div>
            </div>
          )}

          {/* Courier Details */}
          {isCourier && (
            <div className="form-section courier-section">
              <h3>
                <FontAwesomeIcon icon={faBox} /> Courier Details
              </h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Courier Name</label>
                  <input
                    type="text"
                    name="smallShipmentDetails.courierName"
                    value={formData.smallShipmentDetails?.courierName || ""}
                    onChange={handleChange}
                    placeholder="e.g., DHL, FedEx"
                  />
                </div>
                <div className="form-group">
                  <label>Courier Tracking Number</label>
                  <input
                    type="text"
                    name="smallShipmentDetails.courierTrackingNumber"
                    value={
                      formData.smallShipmentDetails?.courierTrackingNumber || ""
                    }
                    onChange={handleChange}
                    placeholder="Tracking number"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faWeightScale} /> Package Weight (kg)
                  </label>
                  <input
                    type="number"
                    name="smallShipmentDetails.packageWeight"
                    value={formData.smallShipmentDetails?.packageWeight || 0}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Products */}
          <div className="form-section">
            <h3>
              <FontAwesomeIcon icon={faBox} /> Products (
              {formData.products.length} items, {calculateTotalWeight()} kg)
              <span className="origin-summary">
                <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country} Origin
              </span>
            </h3>

            <div className="product-selector">
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label>Search/Select Product</label>
                  <input
                    type="text"
                    placeholder="Search for a product..."
                    value={productSearch}
                    onChange={(e) => handleProductSearch(e.target.value)}
                    onFocus={() => setShowProductSelector(true)}
                  />
                  {showProductSelector && productSearch && (
                    <div className="product-search-results">
                      {filteredProducts.slice(0, 10).map((p) => (
                        <div
                          key={p._id}
                          className={`search-result ${selectedProduct?._id === p._id ? "selected" : ""}`}
                          onClick={() => {
                            setSelectedProduct(p);
                            setProductSearch(p.name);
                          }}
                        >
                          {p.name} - ${p.basePrice?.usd || p.pricing?.usd || 0}/
                          {p.unit || "kg"}
                          <span className="product-origin">
                            <FontAwesomeIcon icon={faGlobe} />{" "}
                            {p.origin || SUPPLIER.country}
                            {p.origin === SUPPLIER.country && " 🇲🇾"}
                          </span>
                        </div>
                      ))}
                      {filteredProducts.length === 0 && (
                        <div className="search-result no-results">
                          No products found
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Quantity ({selectedProduct?.unit || "kg"})</label>
                  <input
                    type="number"
                    value={productQuantity}
                    onChange={(e) =>
                      setProductQuantity(
                        Math.max(1, parseInt(e.target.value) || 1),
                      )
                    }
                    min="1"
                    step="1"
                  />
                </div>
                <div
                  className="form-group"
                  style={{ justifyContent: "flex-end" }}
                >
                  <button
                    type="button"
                    className="add-product-btn"
                    onClick={handleAddProduct}
                    disabled={!selectedProduct}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add
                  </button>
                </div>
              </div>
            </div>

            {formData.products.length > 0 && (
              <div className="products-list">
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Qty</th>
                      <th>Unit</th>
                      <th>Price</th>
                      <th>Origin</th>
                      <th>Total</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.products.map((product, index) => (
                      <tr key={index}>
                        <td>{product.name}</td>
                        <td>{product.quantity}</td>
                        <td>{product.unit || "kg"}</td>
                        <td>${(product.price || 0).toFixed(2)}</td>
                        <td>
                          <span className="origin-tag">
                            <FontAwesomeIcon icon={faGlobe} />{" "}
                            {product.origin || SUPPLIER.country}
                            {product.origin === SUPPLIER.country && " 🇲🇾"}
                          </span>
                        </td>
                        <td>
                          $
                          {((product.price || 0) * product.quantity).toFixed(2)}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="remove-btn"
                            onClick={() => handleRemoveProduct(index)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="4"></td>
                      <td>
                        <strong>Total Weight:</strong>
                      </td>
                      <td>
                        <strong>{calculateTotalWeight()} kg</strong>
                      </td>
                      <td></td>
                    </tr>
                    <tr>
                      <td colSpan="4"></td>
                      <td>
                        <strong>From:</strong>
                      </td>
                      <td colSpan="2">
                        <strong>
                          <FontAwesomeIcon icon={faShip} /> {SUPPLIER.name}
                        </strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Customs */}
          <div className="form-section">
            <h3>
              <FontAwesomeIcon icon={faClipboard} /> Customs Information
            </h3>
            <div className="form-row">
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="customs.cleared"
                    checked={formData.customs?.cleared || false}
                    onChange={handleChange}
                  />
                  Cleared
                </label>
              </div>
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="customs.dutiesPaid"
                    checked={formData.customs?.dutiesPaid || false}
                    onChange={handleChange}
                  />
                  Duties Paid
                </label>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Clearance Date</label>
                <input
                  type="date"
                  name="customs.clearanceDate"
                  value={formData.customs?.clearanceDate || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Customs Broker</label>
                <input
                  type="text"
                  name="customs.customsBroker"
                  value={formData.customs?.customsBroker || ""}
                  onChange={handleChange}
                  placeholder="Broker name"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Duty Amount (USD)</label>
              <input
                type="number"
                name="customs.dutyAmount"
                value={formData.customs?.dutyAmount || 0}
                onChange={handleChange}
                min="0"
                step="0.01"
              />
            </div>
            <div className="form-group">
              <label>Port of Entry</label>
              <input
                type="text"
                value={SUPPLIER.portDischarge}
                disabled
                className="disabled-input"
              />
              <small className="helper-text">Default: {SUPPLIER.portDischarge}</small>
            </div>
          </div>

          {/* Notes */}
          <div className="form-section">
            <h3>
              <FontAwesomeIcon icon={faInfoCircle} /> Notes
            </h3>
            <div className="form-group">
              <label>Public Notes</label>
              <textarea
                name="notes"
                value={formData.notes || ""}
                onChange={handleChange}
                rows="2"
                placeholder="Notes visible to customers..."
              />
            </div>
            <div className="form-group">
              <label>Internal Notes</label>
              <textarea
                name="internalNotes"
                value={formData.internalNotes || ""}
                onChange={handleChange}
                rows="2"
                placeholder="Internal notes only visible to admin..."
              />
            </div>
            <div className="form-group">
              <label>Tracking URL</label>
              <input
                type="url"
                name="trackingUrl"
                value={formData.trackingUrl || ""}
                onChange={handleChange}
                placeholder="https://tracking.example.com/..."
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Creating...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} /> Create Shipment
                </>
              )}
            </button>
          </div>
        </form>

        <style>{`
          .shipment-form { max-height: 80vh; overflow-y: auto; padding: 0 0.5rem; }
          .form-section {
            background: #f8f9fa; padding: 1.25rem; border-radius: 8px;
            margin-bottom: 1.25rem; border: 1px solid #e5e5e5;
          }
          .form-section h3 {
            color: #006b2e; margin-bottom: 1rem; font-size: 1rem;
            display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;
          }
          .form-section h3 svg { color: #d9a321; }
          .form-section h4 {
            color: #006b2e; margin: 1rem 0 0.75rem; font-size: 0.9rem;
          }
          .form-row {
            display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem;
          }
          .form-group {
            display: flex; flex-direction: column; gap: 0.25rem;
          }
          .form-group label {
            font-weight: 500; font-size: 0.813rem; color: #1f1f1f;
          }
          .form-group label svg { margin-right: 0.5rem; color: #d9a321; }
          .form-group input, .form-group select, .form-group textarea {
            padding: 0.5rem 0.75rem;
            border: 1px solid #e5e5e5; border-radius: 6px;
            font-size: 0.875rem; background: #fff; color: #1f1f1f;
            width: 100%;
          }
          .form-group input:focus, .form-group select:focus, .form-group textarea:focus {
            outline: none; border-color: #d9a321;
            box-shadow: 0 0 0 3px rgba(217,163,33,0.15);
          }
          .form-group textarea { resize: vertical; min-height: 60px; }
          .form-group input.disabled-input {
            background: #f0f0f0; cursor: not-allowed;
          }
          .helper-text { font-size: 0.65rem; color: #9ca3af; margin-top: 0.125rem; }

          .checkbox-label {
            display: flex; align-items: center; gap: 0.5rem;
            cursor: pointer; font-size: 0.875rem;
          }
          .checkbox-label input[type="checkbox"] {
            width: 18px; height: 18px; cursor: pointer; accent-color: #d9a321;
          }

          .bulk-section, .courier-section { border-left: 4px solid #d9a321; }
          .bulk-tag {
            font-size: 0.6rem; padding: 0.1rem 0.5rem;
            border-radius: 10px; background: #fef3c7; color: #d97706;
          }

          .product-search-results {
            position: absolute; background: #fff; border: 1px solid #e5e5e5;
            border-radius: 6px; max-height: 200px; overflow-y: auto;
            z-index: 100; width: 100%; box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          }
          .search-result {
            padding: 0.5rem 0.75rem; cursor: pointer;
            display: flex; justify-content: space-between; align-items: center;
            transition: background 0.2s ease;
          }
          .search-result:hover { background: #f0f0f0; }
          .search-result.selected { background: #e8f5e9; }
          .search-result.no-results { color: #6b7280; font-style: italic; }
          .product-origin { font-size: 0.65rem; color: #9ca3af; }

          .add-product-btn {
            padding: 0.5rem 1rem; background: #006b2e; color: #fff;
            border: none; border-radius: 6px; cursor: pointer;
            font-weight: 500; display: flex; align-items: center;
            gap: 0.5rem; height: 38px; margin-top: auto;
          }
          .add-product-btn:hover:not(:disabled) { background: #014421; }
          .add-product-btn:disabled { opacity: 0.5; cursor: not-allowed; }

          .items-table { width: 100%; border-collapse: collapse; font-size: 0.813rem; }
          .items-table th {
            background: #f0f0f0; padding: 0.5rem 0.75rem;
            text-align: left; font-weight: 600; color: #1f1f1f;
          }
          .items-table td { padding: 0.5rem 0.75rem; border-bottom: 1px solid #e5e5e5; }
          .items-table tfoot td { font-weight: 600; padding-top: 0.75rem; border-top: 2px solid #e5e5e5; }
          .origin-tag { font-size: 0.65rem; color: #16a34a; }

          .remove-btn {
            background: none; border: none; color: #dc2626;
            cursor: pointer; padding: 0.25rem 0.5rem; border-radius: 4px;
          }
          .remove-btn:hover { background: #fee2e2; }

          .form-actions {
            display: flex; gap: 0.75rem; justify-content: flex-end;
            padding-top: 1rem; border-top: 1px solid #e5e5e5;
            margin-top: 1rem;
          }
          .btn-primary {
            padding: 0.5rem 1.5rem; background: #d9a321; color: #fff;
            border: none; border-radius: 6px; cursor: pointer;
            font-weight: 600; display: flex; align-items: center;
            gap: 0.5rem; transition: all 0.3s ease;
          }
          .btn-primary:hover:not(:disabled) {
            background: #b8860b; transform: translateY(-2px);
          }
          .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
          .btn-secondary {
            padding: 0.5rem 1.5rem; background: #f0f0f0;
            color: #1f1f1f; border: 1px solid #e5e5e5;
            border-radius: 6px; cursor: pointer; font-weight: 500;
          }
          .btn-secondary:hover { background: #e5e5e5; }

          .alert-message {
            padding: 0.75rem 1rem; border-radius: 6px;
            margin: 0 1.5rem 1rem; display: flex; align-items: center; gap: 0.5rem;
          }
          .alert-message.error { background: #fee2e2; color: #dc2626; border: 1px solid #fecaca; }
          .alert-message.success { background: #dcfce7; color: #16a34a; border: 1px solid #bbf7d0; }

          .supplier-info-card {
            background: linear-gradient(135deg, #006B2E, #014421);
            padding: 0.75rem 1.25rem; border-radius: 12px;
            display: flex; align-items: center; gap: 1rem;
            color: #ffffff; margin-bottom: 1rem;
          }
          .supplier-info-card .supplier-icon {
            font-size: 1.5rem; color: #D9A321;
          }
          .supplier-info-card .supplier-info { flex: 1; }
          .supplier-info-card .supplier-info strong {
            font-size: 0.875rem; font-family: "Montserrat", sans-serif;
          }
          .supplier-info-card .supplier-info span {
            font-size: 0.65rem; opacity: 0.8; display: block;
          }
          .supplier-info-card .supplier-info span svg { margin: 0 0.25rem; }
          .supplier-badge {
            font-size: 0.6rem; background: rgba(217,163,33,0.2);
            color: #D9A321; padding: 0.15rem 0.6rem; border-radius: 12px;
            display: flex; align-items: center; gap: 0.25rem;
          }

          .supplier-tag {
            font-size: 0.65rem; padding: 0.15rem 0.5rem;
            border-radius: 12px; background: #dcfce7; color: #16a34a;
            display: inline-flex; align-items: center; gap: 0.25rem;
          }

          .origin-summary {
            font-size: 0.65rem; padding: 0.1rem 0.4rem;
            border-radius: 10px; background: #f0f9ff; color: #3b82f6;
            margin-left: 0.5rem;
          }

          .modal-overlay {
            position: fixed; top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(0,0,0,0.5); display: flex; align-items: center;
            justify-content: center; z-index: 1000; padding: 1rem;
          }
          .modal-content {
            background: #fff; border-radius: 12px;
            max-width: 900px; width: 100%; max-height: 95vh;
            display: flex; flex-direction: column; overflow: hidden;
          }
          .modal-content.large { max-width: 1100px; }
          .modal-header {
            display: flex; justify-content: space-between; align-items: center;
            padding: 1.25rem 1.5rem; border-bottom: 1px solid #e5e5e5; flex-shrink: 0;
            flex-wrap: wrap; gap: 0.5rem;
          }
          .modal-header h2 {
            color: #006b2e; font-size: 1.25rem; margin: 0;
            display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;
          }
          .modal-header h2 svg { color: #d9a321; margin-right: 0.5rem; }
          .close-btn {
            background: none; border: none; font-size: 1.25rem;
            cursor: pointer; color: #6b7280;
            padding: 0.25rem 0.5rem; border-radius: 4px;
          }
          .close-btn:hover { background: #f0f0f0; }

          @media (max-width: 768px) {
            .form-row { grid-template-columns: 1fr; }
            .modal-content.large { max-width: 100%; margin: 0.5rem; }
            .supplier-info-card { flex-wrap: wrap; justify-content: center; text-align: center; }
            .items-table { font-size: 0.75rem; }
            .items-table th, .items-table td { padding: 0.3rem 0.4rem; }
          }
          @media (max-width: 480px) {
            .modal-header h2 { font-size: 1rem; }
            .form-section { padding: 0.75rem; }
            .form-actions { flex-direction: column; }
            .form-actions button { width: 100%; justify-content: center; }
          }
        `}</style>
      </div>
    </div>
  );
};

export default ShipmentForm;